import React, { useEffect } from "react";
import { Stack, Box, BoxProps, IconButton, Typography, styled, InputBase } from "@mui/material";
import CopyAllOutlinedIcon from "@mui/icons-material/CopyAllOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AddIcon from "@mui/icons-material/Add";
import { produce } from "immer";
import { useTranslation } from "react-i18next";

export interface NoteProps extends BoxProps {
  title?: string;
  desc?: string;
  editable?: boolean;
  onTextChange?: (title: string, desc: string) => void;
  onDelete?: () => void;
  onAdd?: () => void;
  onCopy?: () => void;
}

const Note: React.FC<NoteProps> = (props) => {
  const { t } = useTranslation();
  const { title = "", desc = "", editable = false, onTextChange, onDelete, onCopy, onAdd, ...otherProps } = props;

  return (
    <StyledNote isEmpty={!title.length && !desc.length} {...otherProps}>
      <NoteTop>
        {editable ? (
          <>
            <TitleInputBase
              placeholder={t("field:placeholder.addNoteTitle")}
              value={title}
              onChange={(e) => {
                onTextChange && onTextChange(e.target.value, desc);
              }}
              multiline
            />
            <DescInputBase
              placeholder={t("field:placeholder.startTyping")}
              value={desc}
              onChange={(e) => {
                onTextChange && onTextChange(title, e.target.value);
              }}
              multiline
            />
          </>
        ) : (
          <>
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
              {title}
            </Typography>
            <Typography color="text.secondary">{desc}</Typography>
          </>
        )}
      </NoteTop>
      <NoteFooter>
        <Stack spacing={0.5} direction="row">
          {onCopy && (
            <IconButton onClick={() => onCopy()}>
              <CopyAllOutlinedIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            color="error"
            onClick={() => {
              onDelete ? onDelete() : onTextChange && onTextChange("", "");
            }}
          >
            <DeleteOutlineOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>

        {onAdd && (
          <Stack spacing={0.5} direction="row">
            <IconButton color="primary" onClick={onAdd} disabled={title === ""}>
              <AddIcon sx={{ mr: 0.5 }} />
              <Typography variant="body1">{t("common:add")}</Typography>
            </IconButton>
          </Stack>
        )}
      </NoteFooter>
    </StyledNote>
  );
};

export default Note;

const StyledNote = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isEmpty" && prop !== "onTextChange",
})<{ isEmpty: boolean }>(({ theme, isEmpty }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: isEmpty ? "transparent" : theme.palette.backgroundAlpha.main,
  clipPath: `polygon(0 0, 100% 0, 100% 100%, 20px 100%, 0 calc(100% - 20px))`,
  borderRadius: 8,
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
  transition: "background .3s",

  "&:after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: 20,
    height: 20,
    background: theme.palette.backgroundAlpha.border,
  },
}));

const NoteTop = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  flex: 1,
  borderBottom: "none",
  borderTopLeftRadius: 8,
  borderTopRightRadius: 8,
  overflow: "hidden",
}));

const NoteFooter = styled(Box)(({ theme }) => ({
  position: "relative",
  display: "flex",
  justifyContent: "space-between",
  padding: theme.spacing(2),
  paddingTop: theme.spacing(1),
  borderTop: "none",

  ".MuiPushPinIcon-root": {
    transform: "rotate(45deg)",
  },
}));

const TitleInputBase = styled(InputBase)(({ theme }) => ({
  width: "100%",
  ...theme.typography.subtitle2,
  fontWeight: 500,
  "textarea::placeholder": {
    fontWeight: 400,
  },
}));

const DescInputBase = styled(InputBase)(({ theme }) => ({
  width: "100%",
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  "textarea::placeholder": {
    fontWeight: 400,
  },
}));
